/**
=========================================================
* Argon Dashboard 2 MUI - v3.0.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-material-ui
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @mui material components
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import { Grid, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// Argon Dashboard 2 MUI components
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonButton from "components/ArgonButton";
import ArgonInput from "components/ArgonInput";

// Argon Dashboard 2 MUI examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import Table from "examples/Tables/Table";

// Data
import AllVideosTableData from "Views/UsefulCorners/AllVideos/data/AllVideosTableData";
import DeleteDialog from "components/DeleteDialog/DeleteDialog";

import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "utils/api";
function Tables() {
  const [videos, setVideos] = useState([]);
  const [filteredVideos, setFilteredVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedVideoId, setSelectedVideoId] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);

  useEffect(() => {
    fetchVideos();
  }, []);

  useEffect(() => {
    // Filter videos based on search query
    if (searchQuery.trim() === "") {
      setFilteredVideos(videos);
    } else {
      const filtered = videos.filter(
        (video) =>
          video.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          video.videoType?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          video.description?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredVideos(filtered);
    }
  }, [searchQuery, videos]);

  const fetchVideos = async () => {
    try {
      setLoading(true);
      const response = await api.get("/videos");
      if (response.data.success) {
        setVideos(response.data.data || []);
        setFilteredVideos(response.data.data || []);
      } else {
        toast.error("Failed to fetch videos");
      }
    } catch (error) {
      console.error("Error fetching videos:", error);
      toast.error(error.response?.data?.message || "Failed to fetch videos");
    } finally {
      setLoading(false);
    }
  };

  const handleViewClick = (video) => {
    setSelectedVideo(video);
    setViewOpen(true);
  };

  const handleViewClose = () => {
    setViewOpen(false);
    setSelectedVideo(null);
  };

  const handleDeleteClick = (id) => {
    setSelectedVideoId(id);
    setDeleteOpen(true);
  };

  const handleDeleteClose = () => {
    setDeleteOpen(false);
    setSelectedVideoId(null);
  };

  const handleDeleteConfirm = async () => {
    try {
      const response = await api.delete(`/videos/${selectedVideoId}`);
      if (response.data.success) {
        toast.success(response.data.message || "Video deleted successfully");
        fetchVideos(); // Refresh the list
      } else {
        toast.error(response.data.message || "Failed to delete video");
      }
    } catch (error) {
      console.error("Error deleting video:", error);
      toast.error(error.response?.data?.message || "Failed to delete video");
    } finally {
      handleDeleteClose();
    }
  };

  const { columns, rows } = AllVideosTableData(
    filteredVideos,
    handleViewClick,
    handleDeleteClick,
    fetchVideos
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ArgonBox py={3}>
        <ArgonBox mb={3}>
          <Card>
            <ArgonBox p={2}>
              <ArgonBox display="flex" alignItems="center" justifyContent="space-between" p={1}>
                <ArgonTypography mb={2} variant="h6" fontWeight="medium">
                  All Videos {!loading && `(${filteredVideos.length})`}
                </ArgonTypography>
                <ArgonBox display="flex" alignItems="center">
                  <ArgonInput
                    placeholder="search"
                    name="search"
                    size="small"
                    type="text"
                    variant="outlined"
                    sx={{ margin: "10px" }}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  <ArgonButton
                    size="small"
                    component={Link}
                    to="/allvideos/addvideo"
                    variant="contained"
                    color="primary"
                  >
                    Add
                  </ArgonButton>
                </ArgonBox>
              </ArgonBox>
            </ArgonBox>

            <ArgonBox
              sx={{
                "& .MuiTableRow-root:not(:last-child)": {
                  "& td": {
                    borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                      `${borderWidth[1]} solid ${borderColor}`,
                  },
                },
              }}
            >
              {loading ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <CircularProgress color="info" />
                </ArgonBox>
              ) : filteredVideos.length === 0 ? (
                <ArgonBox display="flex" justifyContent="center" alignItems="center" p={3}>
                  <ArgonTypography variant="body2" color="text">
                    {searchQuery ? "No videos found matching your search" : "No videos available"}
                  </ArgonTypography>
                </ArgonBox>
              ) : (
                <Table columns={columns} rows={rows} />
              )}
            </ArgonBox>
          </Card>
        </ArgonBox>
      </ArgonBox>

      {/* Delete Dialog */}
      <DeleteDialog
        open={deleteOpen}
        onClose={handleDeleteClose}
        onConfirm={handleDeleteConfirm}
        title="Delete Video"
        description="Are you sure you want to delete this video? This action cannot be undone."
        cancelText="Cancel"
        confirmText="Delete"
      />

      {/* View Video Dialog */}
      <Dialog
        open={viewOpen}
        onClose={handleViewClose}
        maxWidth="md"
        fullWidth
        sx={{ "& .MuiPaper-root": { borderRadius: "16px", boxShadow: 6 } }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid #eee",
          }}
        >
          <ArgonTypography variant="h5" fontWeight="bold" color="primary">
            🎥 Video Details
          </ArgonTypography>
          <IconButton onClick={handleViewClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {selectedVideo && (
            <ArgonBox py={2}>
              <ArgonBox mb={2}>
                <ArgonTypography variant="caption" fontWeight="bold" color="text">
                  Title:
                </ArgonTypography>
                <ArgonTypography variant="body2" mt={0.5}>
                  {selectedVideo.title}
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox mb={2}>
                <ArgonTypography variant="caption" fontWeight="bold" color="text">
                  Video Type:
                </ArgonTypography>
                <ArgonTypography variant="body2" mt={0.5}>
                  {selectedVideo.videoType}
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox mb={2}>
                <ArgonTypography variant="caption" fontWeight="bold" color="text">
                  Video Link:
                </ArgonTypography>
                <ArgonTypography variant="body2" mt={0.5}>
                  <a href={selectedVideo.videoLink} target="_blank" rel="noopener noreferrer">
                    {selectedVideo.videoLink}
                  </a>
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox mb={2}>
                <ArgonTypography variant="caption" fontWeight="bold" color="text">
                  Description:
                </ArgonTypography>
                <ArgonTypography variant="body2" mt={0.5}>
                  {selectedVideo.description}
                </ArgonTypography>
              </ArgonBox>

              <ArgonBox display="flex" justifyContent="flex-end" mt={3}>
                <ArgonButton variant="outlined" color="secondary" onClick={handleViewClose}>
                  Close
                </ArgonButton>
              </ArgonBox>
            </ArgonBox>
          )}
        </DialogContent>
      </Dialog>

      <Footer />
    </DashboardLayout>
  );
}

export default Tables;
